// Single source of truth for the donation page.
// Imported by both client (form rendering, preview) and server (validation,
// pricing snapshot).

export type ItemType =
  | "brick"
  | "paver"
  | "bench"
  | "light_pole"
  | "ss_sign"
  | "medallion"
  | "coin_copper"
  | "coin_brass";

/** Phone-order info displayed on phone-only tiles (coins). */
export const PHONE_ORDER_NUMBER = "(314) 647-5900";
export const PHONE_ORDER_TEL = "+13146475900";

export type FontChoice = "arial" | "standard_bold" | "times_roman";

export const FONTS: Array<{ id: FontChoice; label: string; cssFamily: string }> = [
  { id: "arial",         label: "Arial (A200)",            cssFamily: "Arial, Helvetica, sans-serif" },
  { id: "standard_bold", label: "Standard Bold (S103FB)",  cssFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' },
  { id: "times_roman",   label: "Times Roman (T101R)",     cssFamily: '"Times New Roman", Times, serif' },
];

/**
 * Engraving UI mode:
 *  - "grid": character-cell grid (brick, paver) — chars/line × lines varies w/ logo.
 *  - "design": free-text "describe your design" textarea (bench, ss_sign, medallion).
 *  - "name": single-line plaque name (light_pole).
 */
export type EngravingMode = "grid" | "design" | "name";

export interface GridConstraint {
  withLogo: { charsPerLine: number; lines: number };
  withoutLogo: { charsPerLine: number; lines: number };
}

export interface DonationItem {
  id: ItemType;
  name: string;
  shortName: string;             // for table/admin contexts
  priceCents: number;
  /** Human-readable price summary, e.g. "$1,000" or "$1,000 (sponsors a pair)". */
  priceLabel: string;
  /** One-line description shown on the gallery card. */
  blurb: string;
  /** Longer description shown when the item form is open. */
  description: string;
  imageUrl: string;
  imageAlt: string;
  /** Omitted on phone-only items (e.g. coins). */
  engraving?: EngravingMode;
  /** Required when engraving === "grid". */
  grid?: GridConstraint;
  /** Whether this item supports a "with logo" variant. */
  supportsLogo: boolean;
  /** Whether the item exposes a font choice in the form. */
  supportsFont: boolean;
  /** Donor may submit a custom vector logo (.eps / .ai) to be used in place of the IBEW seal. */
  supportsCustomLogo?: boolean;
  /** Description-textarea placeholder text (only when engraving === "design"). */
  designPlaceholder?: string;
  /** Sub-header above the engraving section to set expectations. */
  engravingHelp?: string;
  /** When true, no on-site form; the tile prompts a phone call instead (coins). */
  phoneOrderOnly?: boolean;
  /** One-line note shown beneath the phone CTA on phone-only items. */
  phoneOrderInfo?: string;
}

export const DONATION_ITEMS: DonationItem[] = [
  {
    id: "brick",
    name: "8″ × 8″ Engraved Brick",
    shortName: "Brick",
    priceCents: 100_000,
    priceLabel: "$1,000",
    blurb: "Engraved Regimental Red brick set into the museum walkway.",
    description:
      "Laser-engraved 8″ × 8″ Regimental Red brick installed in the Henry Miller Museum walkway. Choose with or without the IBEW logo.",
    imageUrl: "/donations/brick.jpg",
    imageAlt: "Close-up of an engraved Regimental Red brick.",
    engraving: "grid",
    supportsLogo: true,
    supportsFont: true,
    supportsCustomLogo: true,
    grid: {
      withLogo:    { charsPerLine: 20, lines: 3 },
      withoutLogo: { charsPerLine: 20, lines: 6 },
    },
    engravingHelp:
      "Spaces and punctuation count as characters. Please print clearly. The engraver follows your text exactly.",
  },
  {
    id: "paver",
    name: "24″ × 24″ Granite Paver",
    shortName: "Paver",
    priceCents: 500_000,
    priceLabel: "$5,000",
    blurb: "Large granite paver, set into the entrance plaza.",
    description:
      "Laser-engraved 24″ × 24″ granite paver installed at the museum entrance. Choose with or without the IBEW logo.",
    imageUrl: "/donations/paver.jpg",
    imageAlt: "Engraved 24-inch granite paver.",
    engraving: "grid",
    supportsLogo: true,
    supportsFont: true,
    supportsCustomLogo: true,
    grid: {
      withLogo:    { charsPerLine: 18, lines: 5 },
      withoutLogo: { charsPerLine: 18, lines: 10 },
    },
    engravingHelp:
      "Spaces and punctuation count as characters. Please print clearly. The engraver follows your text exactly.",
  },
  {
    id: "bench",
    name: "Granite Bench",
    shortName: "Bench",
    priceCents: 2_500_000,
    priceLabel: "$25,000",
    blurb: "Custom-engraved granite bench placed on museum grounds.",
    description:
      "A granite memorial bench, fully custom: any design, any size, with or without the IBEW logo. Our engraver will work with you on a final proof before production.",
    imageUrl: "/donations/bench.jpg",
    imageAlt: "Granite memorial bench engraved with an IBEW local tribute.",
    engraving: "design",
    supportsLogo: true,
    supportsFont: true,
    supportsCustomLogo: true,
    designPlaceholder:
      "Describe the inscription, layout, and any imagery (e.g. “Local 1 honor roll, names in three columns, IBEW seal centered above”). Our engraver will follow up with a proof.",
    engravingHelp:
      "Bench dedications are fully custom. Tell us in plain language what you'd like: inscription, layout, special imagery. We'll send a proof before production.",
  },
  {
    id: "light_pole",
    name: "Antique Light Pole Sponsorship",
    shortName: "Light Poles",
    priceCents: 500_000,
    priceLabel: "$5,000 (sponsors a pair)",
    blurb: "Sponsor a pair of restored antique light poles with a name plaque.",
    description:
      "Your $5,000 contribution sponsors two restored antique light poles on the museum grounds. Each pair is paired with a recognition plaque bearing the sponsor's name.",
    imageUrl: "/donations/light-pole.jpg",
    imageAlt: "Antique brass recognition plaque mounted on a restored street light.",
    engraving: "name",
    supportsLogo: false,
    supportsFont: false,
    engravingHelp:
      "Sponsorship is recognized with a name plaque on the pole. Provide the name exactly as it should appear (e.g. “In memory of John Doe” or “IBEW Local 1”).",
  },
  {
    id: "ss_sign",
    name: "Stainless Steel Sign",
    shortName: "SS Sign",
    priceCents: 1_000_000,
    priceLabel: "$10,000",
    blurb: "Stainless steel recognition sign mounted on the museum's perimeter fence.",
    description:
      "A laser-engraved stainless steel sign mounted on the perimeter fence of the museum grounds, recognizing a sponsoring local, family, or individual.",
    imageUrl: "/donations/ss-sign.jpg",
    imageAlt: "Stainless steel IBEW Local recognition sign on the museum fence.",
    engraving: "design",
    supportsLogo: true,
    supportsFont: true,
    supportsCustomLogo: true,
    designPlaceholder:
      "Describe what should appear on the sign (e.g. “IBEW Local 9, in memory of our brothers”). The engraver will follow up with a proof.",
    engravingHelp:
      "Signs are fully custom. Provide the wording, logo treatment, and any imagery. We'll send a proof before production.",
  },
  {
    id: "medallion",
    name: "Limestone Medallion",
    shortName: "Medallion",
    priceCents: 1_000_000,
    priceLabel: "$10,000",
    blurb: "Carved limestone medallion set into the museum's facade or grounds.",
    description:
      "A hand-carved limestone medallion installed on the museum facade or grounds, suitable for a local-number tribute, founding-member memorial, or family dedication.",
    imageUrl: "/donations/medallion.jpg",
    imageAlt: "Carved limestone medallion with an IBEW local number.",
    engraving: "design",
    supportsLogo: true,
    supportsFont: true,
    designPlaceholder:
      "Describe the design (e.g. “Local 309 in raised letters, surrounded by the IBEW seal motif”). Our engraver will send a proof.",
    engravingHelp:
      "Medallions are fully custom. Describe the inscription and imagery. We'll send a proof before production.",
  },
  {
    id: "coin_copper",
    name: "Copper Commemorative Coin",
    shortName: "Copper Coin",
    priceCents: 25_000,
    priceLabel: "$250",
    blurb: "Limited-edition copper coin: IBEW founding seal on one side, the boarding house on the other.",
    description:
      "A copper commemorative coin produced by the Electrical Workers Historical Society. One face bears the IBEW founding seal ringed by the names of the ten founders; the reverse shows the Henry Miller boarding house with the inscription \"Where It Began.\"",
    imageUrl: "/donations/coin-copper.jpg",
    imageAlt: "Copper Henry Miller Museum commemorative coin showing the IBEW founding seal.",
    supportsLogo: false,
    supportsFont: false,
    phoneOrderOnly: true,
    phoneOrderInfo: "Credit card or cash accepted, in person or by phone.",
  },
  {
    id: "coin_brass",
    name: "Brass Commemorative Coin",
    shortName: "Brass Coin",
    priceCents: 10_000,
    priceLabel: "$100",
    blurb: "Brass commemorative coin: founders' names ring the seal; the boarding house anchors the reverse.",
    description:
      "A brass commemorative coin produced by the Electrical Workers Historical Society. Same design as the copper edition: the IBEW founding seal ringed by the ten founders' names on one face, the Henry Miller boarding house on the other.",
    imageUrl: "/donations/coin-brass.jpg",
    imageAlt: "Henry Miller Museum commemorative coin showing the boarding house and St. Louis \"Where It Began.\"",
    supportsLogo: false,
    supportsFont: false,
    phoneOrderOnly: true,
    phoneOrderInfo: "Credit card or cash accepted, in person or by phone.",
  },
];

export const ITEM_BY_ID: Record<ItemType, DonationItem> = Object.fromEntries(
  DONATION_ITEMS.map((d) => [d.id, d]),
) as Record<ItemType, DonationItem>;

export function getItem(id: string): DonationItem | null {
  return (ITEM_BY_ID as Record<string, DonationItem | undefined>)[id] ?? null;
}

export function formatPrice(cents: number): string {
  return "$" + (cents / 100).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

/**
 * Maximum allowed line count + char count for a given item + logo choice.
 * Returns null for non-grid items.
 */
export function gridLimitsFor(item: DonationItem, withLogo: boolean): { lines: number; charsPerLine: number } | null {
  if (item.engraving !== "grid" || !item.grid) return null;
  return withLogo ? item.grid.withLogo : item.grid.withoutLogo;
}
